import { useState } from 'react'
import { catalog as catalogApi, leases as leaseApi } from '@/api/endpoints'
import type { Catalog, Lease, LeaseChangeRequest, LeasePowerAction } from '@/api/types'
import { Choice } from '@/components/Choice'
import { Err } from '@/components/Err'
import { Empty } from '@/components/layout/Page'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePoll } from '@/hooks/usePoll'
import { gpuMemoryFloorGb, roomModelItem, roomModels } from '@/domain/flavor'
import { CHANGE_REASONS, MAX_GPUS, changeLines, GPU_NONE } from '@/domain/lease'
import { floorPastQuotaSentence, memoryLeftMb } from '@/domain/quota'
import { size, sizeMb } from '@/ui/format'
import { ChangeLines, Note, NowTo, SpecCard } from '../vm/SpecCards'
import { QuotaBox } from '../vm/QuotaBox'

// 가진 VM 의 변경 신청(B-27·B-35). 적는 것은 사양 이름이 아니라 **VM 이 가질 숫자**다 —
// 코어·메모리는 지금 값이 채워져 있고 내려 적을 수도 있다. 카드는 모델과 목표 장 수이고,
// 모델을 고르지 않는 것이 곧 목표 0 장이고 그 자리는 `없음` 이다.
// `모델 무관` 은 없다: 모델이 없으면 어떤 하드웨어가 올지 정해지지 않아 백엔드도 400 으로 거절한다.
// 디스크 칸은 없다 — 데이터 디스크는 승인 없이 소유자가 VM 상세에서 바로 늘린다.
//
// 본문은 자원마다 카드다(`../vm/SpecCards.tsx`) — 머리 오른쪽이 그 VM 의 노드에서 가질 수 있는 끝(`최대 n코어`), 본문이 `지금 → 바꿀 값`,
// 맨 아래 `바뀌는 것` 이 보내면 바뀌는 것을 줄마다 말한다.
//
// 고를 수 있는 것은 VM 이 있는 노드에 남은 것이다(`leases.room`) — 변경은 그 노드에서만 코어·메모리·카드를 받고
// VM 을 옮기는 길이 없다. 모델 목록과 남은 장 수도 그 노드의 것이고, 넘는 숫자는 백엔드가 409 로 거절하므로 여기서 먼저 막는다.
// 다른 노드에만 있는 모델은 빼지 않고 잠긴 채 `이 노드에 없음` 이라 말한다 — 빼면 사람은 그 모델이 왜 없는지 모른다.
//
// 카드가 요구하는 메모리는 사양 만들기(`views/Catalog.tsx`)와 같은 함수로 세고 그 아래는
// 여기서 막는다 — 눌러서 거절로 알게 하지 않는다.
//
// 같은 창이 최고 관리자의 `사양 수정`(B-43)이기도 하다(`set`). 카드는 같고 사유·쿼터 칸이 없다 —
// 최고 관리자의 수정은 직위 한도에 묶이지 않고 기다릴 승인도 없다. 비운 메모리는 지금 값이라 카드의 하한 아래면
// 막는다(서버도 올리지 않고 거절한다). 저장한 뒤 같은 창이 지금 재부팅해 적용할지, 다음 재부팅에 맡길지 묻는다.

const CATALOG_POLL_MS = 60000
const ROOM_POLL_MS = 15000
// 쥔 카드의 모델이 임대 행에 없는 VM 의 `지금 그대로`. 모델 이름으로 가리킬 수 없는 상태라
// 이 자리에만 값 하나를 더 둔다 — 대화 상자가 `없음` 을 고른 채로 열리면
// 코어만 바꾸려던 첫 제출이 카드를 떼자고 말한다.
const KEEP = 'keep'

export function ChangeDialog({
  lease,
  open,
  onClose,
  onDone,
  set = false,
}: {
  lease: Lease
  open: boolean
  onClose: () => void
  onDone: () => void
  /** 최고 관리자의 사양 수정(B-43) — 사유·쿼터 없이 바로 정하고, 저장 뒤 적용 시점을 묻는다. */
  set?: boolean
}) {
  const [saved, setSaved] = useState(false)
  const close = () => {
    setSaved(false)
    onClose()
  }
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : saved ? (setSaved(false), onDone()) : close())}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
        {open && saved ? (
          <ApplyNow lease={lease} onDone={() => (setSaved(false), onDone())} />
        ) : open ? (
          <ChangeForm lease={lease} set={set} onClose={close} onDone={set ? () => setSaved(true) : onDone} />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

// 저장한 사양을 언제 적용하는가. 적용은 VM 이 꺼졌다 켜질 때다 — 실행 중이면 재부팅, 꺼져 있으면 실행이 그 자리다.
// `나중에` 는 다음 재부팅(또는 실행)에 맡긴다. 그때까지 상세의 재부팅·실행 칩이 대기 색으로 그것을 말한다.
function ApplyNow({ lease: l, onDone }: { lease: Lease; onDone: () => void }) {
  const running = l.vm?.state === 'running'
  const action: LeasePowerAction = running ? 'reboot' : 'start'
  const word = running ? '재부팅' : '실행'
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const now = async () => {
    setBusy(true)
    setError(null)
    try {
      await leaseApi.power(l.id, action)
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>{l.name} 사양 수정</DialogTitle>
      </DialogHeader>
      <p className="text-[12px] text-muted-foreground">저장했습니다. 다음 {word} 때 적용됩니다.</p>
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone} disabled={busy}>
          나중에
        </Button>
        <Button type="button" onClick={() => void now()} disabled={busy}>
          {busy ? `${word} 중` : `지금 ${word}`}
        </Button>
      </DialogFooter>
    </>
  )
}

function ChangeForm({ lease: l, set, onClose, onDone }: { lease: Lease; set: boolean; onClose: () => void; onDone: () => void }) {
  const catalog = usePoll(() => catalogApi.get(), CATALOG_POLL_MS)
  const cat: Catalog | null = catalog.data
  const room = usePoll(() => leaseApi.room(l.id), ROOM_POLL_MS)
  const r = room.data
  // 지금 값이 채워진 칸이다. 비우면 그 자원은 본문에서 빠져 지금 값이 남는다 — placeholder 가 그 값을 말한다.
  const nowCores = String(l.cores)
  const nowMemoryGb = String(Math.ceil(l.memory_mb / 1024))
  const nowModel = l.gpus > 0 ? (l.gpu_model ?? KEEP) : ''
  const [cores, setCores] = useState(nowCores)
  const [memoryGb, setMemoryGb] = useState(nowMemoryGb)
  const [gpuModel, setGpuModel] = useState(nowModel)
  const [gpus, setGpus] = useState(l.gpus > 0 ? l.gpus : 1)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // 모델은 VM 이 있는 노드의 것이 먼저 서고, 다른 노드에만 있는 모델이 잠긴 채 뒤에 선다. 쥔 모델은 늘 고를 수 있다 —
  // 카탈로그에서 빠진 모델을 쥔 VM 도 그 모델을 골라야 코어 하나라도 줄인다(그때는 한 장의 메모리를 몰라 하한도 없다, 백엔드도 같다).
  const heldModel = nowModel && nowModel !== KEEP ? nowModel : undefined
  const models = roomModels(r?.gpus ?? [], cat?.gpus ?? [], heldModel)
  const picked = models.find((m) => m.model === gpuModel)
  // 한 모델로 가질 수 있는 장 수는 쥔 것과 그 노드가 더 내줄 것의 합이다.
  const maxCards = Math.min(MAX_GPUS, picked?.max ?? 0)
  const pickedGpus = Math.min(gpus, Math.max(maxCards, 1))

  // 보내는 것은 움직이는 숫자뿐이다. 카드는 목표 장 수와 모델이 늘 함께 간다 —
  // 모델만 보내면 백엔드가 400 이고, 장 수만 보내면 어떤 카드를 받을지 정해지지 않는다.
  const targetGpus = gpuModel === KEEP ? l.gpus : gpuModel === '' ? 0 : pickedGpus
  const sameCards = targetGpus === l.gpus && gpuModel === nowModel
  const body: LeaseChangeRequest = { reason: reason.trim() }
  if (cores !== '' && cores !== nowCores) body.cores = Number(cores)
  if (memoryGb !== '' && memoryGb !== nowMemoryGb) body.memory_mb = Number(memoryGb) * 1024
  if (!sameCards) {
    body.gpus = targetGpus
    if (targetGpus > 0) body.gpu_model = gpuModel
  }
  // 목표 카드가 요구하는 메모리. 비운 칸은 백엔드가 하한까지 올리므로 막지 않고,
  // 적힌 숫자가 그 아래면 막는다 — 지금 값이 채워져 있어 가만히 두면 그 숫자가 간다.
  const floorGb = gpuMemoryFloorGb(picked?.vramMb ?? 0, targetGpus)
  // 최고 관리자의 수정은 서버가 하한까지 올려 주지 않는다 — 비운 칸은 지금 값이 가므로 그 값으로 잰다.
  const typedGb = memoryGb !== '' ? Number(memoryGb) : set ? l.memory_mb / 1024 : null
  const memoryBad = floorGb > 0 && typedGb !== null && typedGb < floorGb
  // 받아들여지면 VM 이 가질 메모리. 신청의 비운 칸과 하한 아래의 칸은 백엔드가 하한까지 올리는 값으로 센다.
  const targetMb = set
    ? memoryGb === '' ? l.memory_mb : Number(memoryGb) * 1024
    : Math.max(memoryGb === '' ? l.memory_mb : Number(memoryGb) * 1024, floorGb * 1024)
  const targetCores = cores === '' ? l.cores : Number(cores)

  // 그 노드에 남은 만큼만 늘린다 — 넘으면 백엔드가 409 로 거절한다. 노드를 재지 못하면 막지 않는다(적용이 판정한다).
  const maxCores = r?.cores != null ? l.cores + r.cores : null
  const maxMemoryGb = r?.memory_mb != null ? Math.floor((l.memory_mb + r.memory_mb) / 1024) : null
  const coresBad = maxCores !== null && targetCores > Math.max(maxCores, l.cores)
  const memoryOver = maxMemoryGb !== null && memoryGb !== '' && Number(memoryGb) > Math.max(maxMemoryGb, Math.ceil(l.memory_mb / 1024))
  const cardsBad = gpuModel !== '' && gpuModel !== KEEP && gpuModel !== nowModel && maxCards === 0

  // 카드를 남기는 변경이 늘리는 메모리가 내 메모리 쿼터에 남은 것보다 크면 알린다(F-101) — 막지 않는다.
  const left = cat?.quota ? memoryLeftMb(cat.quota) : null
  const floorPastQuota = targetGpus > 0 && floorGb > 0 && left !== null && targetMb - l.memory_mb > left
  const nothing = body.cores === undefined && body.memory_mb === undefined && body.gpus === undefined
  const disabled = busy || nothing || memoryBad || coresBad || memoryOver || cardsBad || (!set && reason.trim() === '')
  const lines = nothing
    ? []
    : changeLines(
        { cores: l.cores, memory_mb: l.memory_mb, gpus: l.gpus, gpu_model: l.gpu_model },
        { cores: targetCores, memory_mb: targetMb, gpus: targetGpus, gpu_model: gpuModel === KEEP ? l.gpu_model : gpuModel || null },
      )

  const submit = async () => {
    if (disabled) return
    setBusy(true)
    setError(null)
    try {
      if (set) {
        await leaseApi.setSpec(l.id, {
          ...(body.cores !== undefined ? { cores: body.cores } : {}),
          ...(body.memory_mb !== undefined ? { memory_mb: body.memory_mb } : {}),
          ...(body.gpus !== undefined ? { gpus: body.gpus } : {}),
          ...(body.gpu_model !== undefined ? { gpu_model: body.gpu_model } : {}),
        })
      } else {
        await leaseApi.requestChange(l.id, body)
      }
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  const modelItems = [
    ...(nowModel === KEEP ? [{ value: KEEP, title: `지금 카드 ${l.gpus}장 그대로` }] : []),
    { value: '', title: GPU_NONE },
    ...models.map((m) => roomModelItem(m, heldModel)),
  ]
  const countItems = Array.from({ length: Math.max(maxCards, 1) }, (_, i) => ({ value: String(i + 1), title: `${i + 1}장` }))
  const q = cat?.quota
  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {l.name} {set ? '사양 수정' : '변경 신청'}
        </DialogTitle>
      </DialogHeader>
      {catalog.error ? <Err error={catalog.error} /> : null}
      {room.error ? <Err error={room.error} reasons={CHANGE_REASONS} /> : null}
      {(!cat || !r) && !catalog.error && !room.error ? <Empty>불러오는 중</Empty> : null}
      {cat && r ? (
        <div className="grid gap-3">
          <SpecCard title="CPU" aside={maxCores !== null ? `최대 ${maxCores}코어` : undefined}>
            <NowTo id="change-cores" label="코어" now={`${l.cores}코어`} unit="코어" value={cores} placeholder={nowCores} busy={busy} bad={coresBad} onChange={setCores} />
            {coresBad ? <Note bad>이 노드에 남은 코어는 {r?.cores}코어입니다.</Note> : null}
          </SpecCard>
          <SpecCard title="메모리" aside={maxMemoryGb !== null ? `최대 ${size(maxMemoryGb)}` : undefined}>
            <NowTo
              id="change-memory"
              label="메모리 (GB)"
              now={sizeMb(l.memory_mb)}
              unit="GB"
              value={memoryGb}
              placeholder={nowMemoryGb}
              busy={busy}
              bad={memoryBad || memoryOver}
              onChange={setMemoryGb}
            />
            {floorGb > 0 ? (
              <Note bad={memoryBad}>
                {gpuModel} {targetGpus}장은 {floorGb}GB 이상
              </Note>
            ) : null}
            {memoryOver ? <Note bad>이 노드에 남은 메모리는 {sizeMb(r?.memory_mb)}입니다.</Note> : null}
          </SpecCard>
          <SpecCard title="GPU" aside={picked?.onNode && gpuModel !== '' ? `최대 ${picked.max}장` : undefined}>
            <div className="grid gap-1.5">
              <Label htmlFor="change-gpu-model">모델</Label>
              <Choice id="change-gpu-model" value={gpuModel} items={modelItems} disabled={busy} onChange={setGpuModel} />
            </div>
            {gpuModel === '' || gpuModel === KEEP ? null : (
              <div className="grid gap-1.5">
                <Label htmlFor="change-gpus">장 수</Label>
                <Choice id="change-gpus" compact value={String(pickedGpus)} items={countItems} disabled={busy || maxCards === 0} onChange={(v) => setGpus(Number(v))} />
              </div>
            )}
          </SpecCard>
          <ChangeLines lines={lines} />
          {set ? null : (
            <div className="grid gap-1.5">
              <Label htmlFor="change-reason">사유</Label>
              <Input id="change-reason" value={reason} maxLength={200} autoComplete="off" disabled={busy} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
          {q && !set ? (
            <QuotaBox
              quota={q}
              axes={['cores', 'memory_mb']}
              add={{ cores: Math.max(0, targetCores - l.cores), memory_mb: Math.max(0, targetMb - l.memory_mb) }}
              waived={targetGpus > 0 ? ['memory_mb'] : []}
            />
          ) : null}
          {floorPastQuota && !set ? <p className="text-[12px] text-warn-ink">{floorPastQuotaSentence(floorGb * 1024)}</p> : null}
          {set ? null : <p className="text-[12px] text-muted-foreground">승인되면 다음 재부팅 때 적용됩니다.</p>}
        </div>
      ) : null}
      <Err error={error} reasons={CHANGE_REASONS} />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
          취소
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={disabled || !cat || !r}>
          {set ? (busy ? '저장 중' : '저장') : busy ? '신청 중' : '신청'}
        </Button>
      </DialogFooter>
    </>
  )
}
